import { computed, isRef, ref, inject, provide } from '@vue/composition-api'

const effectTypesSymbol = Symbol('effectTypesSymbol')

function isMultipleSelect (field) {
  return ([ 'select', 'attributeSelect' ].includes(field.questionType) && field.config.multiple) ||
    (field.questionType === 'departmentSelect' && field.config.multiple !== false)
}

export default function useEffectTypes ({ updateValue, updateField, field, isReadonly = false } = {}) {
  const BASE_EFFECT_TYPES = [
    {
      label: 'Update Field Value',
      key: 'valueChange',
      handler: valueChangeEffect,
      fieldRequirements: [],
    },
    {
      label: 'Update Field Config',
      key: 'configChange',
      handler: configChangeEffect,
      fieldRequirements: [],
    },
    {
      label: 'Update Field Validation',
      key: 'validationChange',
      handler: validationChangeEffect,
      fieldRequirements: [],
    },
    {
      label: 'Toggle Field Value',
      key: 'valueToggle',
      handler: valueToggleEffect,
      // Only allow for fields that are multi-selects
      fieldRequirements: [ isMultipleSelect ],
    },
  ]
  const effectTypesArray = inject(effectTypesSymbol, ref(BASE_EFFECT_TYPES))
  provide(effectTypesSymbol, effectTypesArray)

  const filteredEffectTypesArray = computed(() => effectTypesArray.value
    .filter(effectType => {
      const fieldRequirements = effectType.fieldRequirements || []

      return fieldRequirements.every(requirement => requirement(field.value))
    })
  )

  if (!isRef(isReadonly)) isReadonly = ref(isReadonly)

  const effectTypes = computed(() => effectTypesArray.value
    .reduce((types, type) => ({ ...types, [type.key]: type }), {}))

  function addTypes (typeOrTypes) {
    const types = Array.isArray(typeOrTypes) ? typeOrTypes : [ typeOrTypes ]
    effectTypesArray.value.push(...types)
  }

  function triggerEffects (effects, actionId, { sourceValues }) {
    effects.forEach(triggerEffect)

    function triggerEffect ({ type, value }, index) {
      const effect = effectTypes.value[type].handler
      effect(value, `${actionId}_${index}`, { sourceValues, field, updateField, updateValue })
    }
  }

  function valueChangeEffect (value, actionId, { sourceValues, updateValue }) {
    // NOTE: Do not trigger when readonly form
    if (isReadonly.value) return
    let newValue = value
    if (typeof value === 'string' && value.includes('$_')) {
      const index = Number(value.slice(2))
      newValue = sourceValues[index]
    }
    if (typeof value === 'number' && value === 9876543210) {
      newValue = sourceValues[0]
    }
    updateValue(newValue)
  }

  function valueToggleEffect (value, actionId, { updateField }) {
    // NOTE: We allow this change in readonly mode as it is filtered out later in useFieldActions
    updateField(value, actionId)
  }

  function validationChangeEffect (value, actionId, { updateField }) {
    // NOTE: Do not trigger when readonly form
    if (isReadonly.value) return
    updateField(value, actionId)
  }

  function configChangeEffect (value, actionId, { updateField }) {
    updateField(value, actionId)
  }

  return {
    effectTypes,
    effectTypesArray,
    filteredEffectTypesArray,
    addTypes,
    triggerEffects,
  }
}
